// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited
//
// Background tracking for the Agency Fulfilment Call Bot (STSMPD-1323).
//
// The AgencyCallModal short-polls a call only while it is open. But Ops routinely place a call and
// then close the modal to review other shifts (they shouldn't have to babysit a modal — otherwise
// they may as well make the call themselves). Without this saga the row's status pill would freeze
// at whatever state the modal last saw (typically "Answered") and never advance to the terminal
// "Outcome".
//
// This saga owns badge continuity independently of the modal: on each `trackAgencyCall` (dispatched
// by the modal when a call is created) it polls GET /call-requests/:id to completion and keeps the
// per-allocation entry in the store up to date. It tracks several calls concurrently (one task per
// allocation) and cancels+replaces a prior task if the same allocation is re-called.
//
// The durable, team-visible record remains the flex-core admin alert + admin note + Slack post;
// this only keeps the in-app row pill honest for the current session.

import { call, cancel, delay, fork, put, takeEvery } from 'redux-saga/effects'

import { getCallRequest } from 'syft-acp-core/store/agency-call/api'
import { setAgencyCallState } from 'syft-acp-core/store/agency-call/actions'
import { AGENCY_CALL_TRACK } from 'syft-acp-core/store/agency-call/action-types'
import { TERMINAL_CALL_STATUSES } from 'syft-acp-core/store/agency-call/types'

/** Poll cadence + ceiling — mirrors the modal (~10 minutes) so a stuck call can't poll forever. */
export const POLL_INTERVAL_MS = 3000
export const MAX_POLLS = 200

const isTerminal = status => TERMINAL_CALL_STATUSES.includes(status)

// `getCallRequest` rejects with a status-bearing error (see core/api/call). A 4xx is permanent
// for this call request (e.g. 404 the id is gone / the in-memory service restarted, 401/403 auth),
// so there is no point polling it to the ceiling. Transient failures (network, 5xx, or no status)
// are worth retrying.
const isNonRetryableStatus = status => typeof status === 'number' && status >= 400 && status < 500

/**
 * Poll one call request until it reaches a terminal status (or the ceiling), pushing every state
 * change into the store so the row pill stays current. Transient errors are swallowed so a blip
 * doesn't abandon tracking; a non-retryable 4xx stops tracking immediately.
 */
export function* pollUntilTerminal(allocationId, callRequestId) {
  for (let i = 0; i < MAX_POLLS; i += 1) {
    try {
      const view = yield call(getCallRequest, callRequestId)
      yield put(
        setAgencyCallState({
          allocationId,
          lastState: view.last_state,
          status: view.status,
          callRequestId: view.call_request_id,
        }),
      )
      if (isTerminal(view.status)) return
    } catch (e) {
      // Permanent (4xx) -> stop tracking rather than hammering the endpoint to the ceiling.
      // Transient (network / 5xx / no status) -> fall through and keep polling.
      if (isNonRetryableStatus(e?.status)) return
    }
    yield delay(POLL_INTERVAL_MS)
  }
}

export default function* root() {
  // One live tracking task per allocation; a re-call cancels the previous task before starting.
  const tasks = {}
  yield takeEvery(AGENCY_CALL_TRACK, function* onTrack({ payload }) {
    const { allocationId, callRequestId } = payload
    if (allocationId == null || !callRequestId) return
    const existing = tasks[allocationId]
    if (existing) {
      yield cancel(existing)
    }
    tasks[allocationId] = yield fork(pollUntilTerminal, allocationId, callRequestId)
  })
}
